import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@shared/components/ui/dropdown-menu';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Search } from '@shared/components/ui/Search';
import { faSliders } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { useIsMobile } from '@shared/hooks/useMobile';
import { cn } from '@shared/utils/utils';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EntriesFiltersPanel } from './EntriesFiltersPanel';

interface FiltersSectionProps {
  handleSearch: (searchTerm: string) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
  filterConditions?: FilterConditionOutput[];
  searchTerm?: string;
  hasTeamData?: boolean;
}

export const FiltersSection = ({
  handleSearch,
  onFilterChange,
  filterConditions = [],
  searchTerm = '',
  hasTeamData = false,
}: FiltersSectionProps) => {
  const { t } = useTranslation('merchandiseV3');
  const isMobile = useIsMobile();
  const modalRef = useRef<ModalCenterRef>(null);
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  // Calcular número de filtros activos
  const activeFiltersCount = useMemo(() => {
    return filterConditions.length;
  }, [filterConditions]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = () => {
    handleSearch(inputValue.trim());
  };

  // Botón trigger para filtros
  const FilterTriggerButton = (
    <div className="relative">
      <Button
        variant="outline"
        type="button"
        size="sm"
        icon={faSliders}
        iconPosition="start"
        className={cn(
          'shadow-none transition-colors border border-input bg-background text-foreground/70 hover:bg-accent hover:text-accent-foreground hover:border-input',
        )}
        data-testid="filters-toggle-button"
        onClick={isMobile ? () => modalRef.current?.openModal() : undefined}
      >
        <span className="hidden sm:block text-sm">{t('filters.title', 'Filtros')}</span>
      </Button>
      {/* Badge de contador de filtros activos */}
      {activeFiltersCount > 0 && (
        <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium shadow-sm z-10">
          {activeFiltersCount}
        </span>
      )}
    </div>
  );

  // Contenido del panel de filtros
  const FiltersContent = (
    <EntriesFiltersPanel initialConditions={filterConditions} onChange={onFilterChange} hasTeamData={hasTeamData} />
  );

  return (
    <div className="flex items-center gap-3">
      <Search
        placeholder={t('list.search.placeholder', 'Buscar entradas...')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />

      {/* Filtros - Desktop: Dropdown, Mobile: Modal */}
      {isMobile ? (
        <>
          {FilterTriggerButton}
          <ModalCenter
            ref={modalRef}
            title={t('filters.title', 'Filtros')}
            type="default"
            width="xl"
            className="w-full h-full max-w-none"
          >
            {FiltersContent}
          </ModalCenter>
        </>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{FilterTriggerButton}</DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="bottom"
            className="sm:min-w-[500px] sm:max-w-[500px] p-4 bg-body-bg text-foreground border border-card-border"
          >
            <div className="flex items-center justify-between mb-3 pt-1">
              <div className="text-sm font-semibold text-foreground">{t('filters.title', 'Filtros')}</div>
            </div>
            {FiltersContent}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
